import React from "react";
import VimeoPlayer from "./VimeoPlayer";

function isEmbeddable(url = "") {
  return /youtube\.com|youtu\.be/.test(url);
}

function isVimeoUrl(url = "") {
  return /vimeo\.com\//.test(url);
}

// Vimeo URLs never reach here - they're always intercepted by isVimeoUrl()
// above and routed through VimeoPlayer instead, so this only ever needs to
// handle YouTube (the one other embeddable source still using a plain
// iframe - see the comment block below for why).
function toEmbedUrl(url) {
  const yt = url.match(/(?:youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  return url;
}

// Right-click is disabled site-wide (see App.jsx), but a few content types
// here need their own extra handling since a parent-document event listener
// literally cannot reach into another document:
//  - A plain YouTube/Vimeo <iframe> is a cross-origin embed - entirely out
//    of our control, no code on our page can touch its context menu. Vimeo
//    videos get routed through VimeoPlayer below instead, which uses the
//    Vimeo Player SDK plus an on-page click shield so the mouse never
//    actually reaches the iframe (see that file for how). YouTube still
//    uses the plain iframe for now - only Vimeo was reported as a problem.
//  - Our own uploaded PDFs render via the browser's built-in PDF viewer
//    (same-origin, but it's the browser's native UI, not a scriptable DOM),
//    so we can't intercept right-click inside it either. `#toolbar=0` (Chrome
//    /Edge) hides the viewer's own download/print controls, and there's no
//    "open in a new tab" escape hatch for students - both close off the
//    easiest, one-click ways around this, even though the native context
//    menu itself can still appear on some browsers.
function noContextMenu(e) {
  e.preventDefault();
  return false;
}

export default function ContentBlockView({ block }) {
  switch (block.type) {
    case "text":
      return (
        <div
          className="content-block content-block-text"
          onContextMenu={noContextMenu}
          dangerouslySetInnerHTML={{ __html: block.data.html || "" }}
        />
      );

    case "video": {
      const url = block.data.url || "";
      if (!url) return <p className="muted">No video URL set.</p>;
      if (isVimeoUrl(url)) {
        // Pass the whole URL through rather than hand-parsing just the
        // numeric ID out of it - a private/unlisted Vimeo video's URL has a
        // privacy hash after the ID (vimeo.com/123456789/abcNDEF987), and
        // dropping that (which a plain `/vimeo\.com\/(\d+)/` match does)
        // is exactly what made the player fail to load the video: the SDK
        // loaded fine and the click-shield still worked, but Vimeo itself
        // refused to serve a private video with no hash. The Vimeo Player
        // SDK's `url` option parses any valid Vimeo URL (with or without a
        // hash) itself, so there's no need to reconstruct one by hand here.
        return (
          <div className="content-block content-block-video">
            <VimeoPlayer videoUrl={url} />
          </div>
        );
      }
      if (isEmbeddable(url)) {
        return (
          <div className="content-block content-block-video">
            <div className="video-embed-wrap">
              <iframe
                src={toEmbedUrl(url)}
                title="Lesson video"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </div>
          </div>
        );
      }
      return (
        <div className="content-block content-block-video" onContextMenu={noContextMenu}>
          <video controls controlsList="nodownload" src={url} style={{ width: "100%", borderRadius: 8 }} />
        </div>
      );
    }

    case "pdf": {
      const url = block.data.url || "";
      if (!url) return <p className="muted">No PDF uploaded.</p>;
      const viewerUrl = `${url}#toolbar=0&navpanes=0`;
      return (
        <div className="content-block content-block-pdf" onContextMenu={noContextMenu}>
          <iframe src={viewerUrl} title={block.data.fileName || "PDF"} className="pdf-frame" />
        </div>
      );
    }

    case "download":
      return (
        <div className="content-block content-block-download">
          <a href={block.data.url} download={block.data.fileName} className="download-card">
            <span className="download-icon">&#8681;</span>
            <span>
              <strong>{block.data.fileName || "Download file"}</strong>
              <div className="muted">Click to download</div>
            </span>
          </a>
        </div>
      );

    case "task":
      return (
        <div className="content-block content-block-task">
          <div className="task-badge">Task</div>
          <p>{block.data.instructions}</p>
        </div>
      );

    default:
      return null;
  }
}
